import { combineReducers, configureStore } from '@reduxjs/toolkit';
import rankReducer from 'app/slices/RankingSlice';
import userReducer from 'app/slices/UserSlice';
import { FLUSH, PAUSE, PERSIST, persistReducer, persistStore, PURGE, REGISTER, REHYDRATE } from 'redux-persist';
import storage from 'redux-persist/lib/storage'; // defaults to localStorage for web
import { migratePersistedState, PERSIST_VERSION } from './persistMigrations';
import appUiReducer from './slices/AppUiSlice';
import categoriesReducer from './slices/CategoriesSlice';
import categoryRankingsReducer from './slices/CategoryRankingsSlice';
import gameScheduleReducer from './slices/GameScheduleSlice';
import nflReducer from './slices/GamesSlice';
import headToHeadReducer from './slices/HeadToHeadSlice';
import teamsReducer from './slices/LeagueSlice';
import navReducer from './slices/NavigationSlice';
import predictionReducer from './slices/PredictionSlice';
import syncReducer from './slices/SyncManagerSlice';
import teamStatsReducer from './slices/TeamStatsSlice';

const persistConfig = {
  key: 'root',
  version: PERSIST_VERSION,
  migrate: migratePersistedState,
  storage,
  whitelist: ['nfl', 'categories'],
};

// type RootState = ReturnType<typeof rootReducer>;

/**
 *
 * ui: uiReducer,
 * ranking: rankReducer,
 * stats: rankReducer,
 * schedule: scheduleReducer,
 * games: gamesReducer,
 * byeweek: byeWeekReducer,
 * league: teamsReducer,
 */

const uiReducers = combineReducers({ app: appUiReducer });

export const rootReducer = combineReducers({
  nav: navReducer,
  nfl: nflReducer,
  league: teamsReducer,
  schedule: gameScheduleReducer,
  stats: rankReducer,
  predictions: predictionReducer,
  ui: uiReducers,
  sync: syncReducer,
  user: userReducer,
  h2h: headToHeadReducer,
  categories: categoriesReducer,
  categoryRankings: categoryRankingsReducer,
  teamStats: teamStatsReducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

// Explicitly enable DevTools for development
const isDevelopment = process.env.NODE_ENV !== 'production';

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
  devTools: isDevelopment && {
    name: 'NFL Stats App',
    trace: false,
    traceLimit: 25,
    maxAge: 50,
    // trace: true,
    // traceLimit: 25,
    // maxAge: 50,
    actionsDenylist: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
    // stateSanitizer:  (state) => state, // Pass through state without sanitization
    stateSanitizer: (state) => ({
      ...state,
      stats: `[Not listed due to DevTools freezing, use ${"window.nflstats.getSlice('stats')"}]`,
    }),
    // stateSanitizer: (state: any) => ({
    //   ...state,
    //   stats: {
    //     ...state.stats,
    //     ranks: '[Omitted from DevTools]',
    //   },
    // }),
  },
});

// Debug log to verify DevTools configuration
if (isDevelopment) {
  console.log('🔧 Redux DevTools enabled:', {
    environment: process.env.NODE_ENV,
    hasExtension: typeof window !== 'undefined' && !!(window as any).__REDUX_DEVTOOLS_EXTENSION__,
  });
}

export type StoreType = typeof store;
export type StoreDispatchType = typeof store.dispatch;
export type StoreGetStateType = typeof store.getState;

// export const store = setupStore(persistedReducer);
export const persistor = persistStore(store);
